import Ajv from 'ajv';
import { ViewDSL, ValidationResult, SectionManifestRegistry, SectionManifest } from './types';

export class DezzyValidator {
  private ajv: Ajv;
  private viewSchema: any;
  private registry: SectionManifestRegistry;

  constructor(viewSchema: any, registry: SectionManifestRegistry = {}) {
    this.ajv = new Ajv({ allErrors: true });
    this.viewSchema = viewSchema;
    this.registry = registry;
  }

  validateView(dsl: ViewDSL): ValidationResult {
    const issues: string[] = [];

    // Schema validation
    const validate = this.ajv.compile(this.viewSchema);
    const valid = validate(dsl);
    
    if (!valid && validate.errors) {
      issues.push(...validate.errors.map(error => 
        `${error.instancePath}: ${error.message}`
      ));
    }

    // Business logic validation
    this.validateBusinessRules(dsl, issues);

    return {
      valid: issues.length === 0,
      errors: issues.length > 0 ? issues : undefined,
    };
  }

  private validateBusinessRules(dsl: ViewDSL, issues: string[]): void {
    // Validate view ID format
    if (!dsl.meta.id.startsWith('view.')) {
      issues.push('View ID must start with "view."');
    }

    // Validate sections exist in registry
    dsl.sections.forEach((section, index) => {
      if (!this.registry[section.use]) {
        issues.push(`Unknown section: ${section.use} at index ${index}`);
      }

      // Validate grid positioning
      if (dsl.layout.type === 'grid' && section.grid) {
        const maxCols = dsl.layout.cols || 12;
        if (section.grid.colStart && section.grid.colStart > maxCols) {
          issues.push(`Section ${index} colStart (${section.grid.colStart}) exceeds grid columns (${maxCols})`);
        }
        if (section.grid.colSpan && section.grid.colSpan > maxCols) {
          issues.push(`Section ${index} colSpan (${section.grid.colSpan}) exceeds grid columns (${maxCols})`);
        }
        if (section.grid.colStart && section.grid.colSpan) {
          const endCol = section.grid.colStart + section.grid.colSpan - 1;
          if (endCol > maxCols) {
            issues.push(`Section ${index} extends beyond grid (ends at column ${endCol}, max is ${maxCols})`);
          }
        }
      }

      // Validate token references
      if (section.bindings) {
        Object.values(section.bindings).forEach(binding => {
          if (typeof binding === 'string' && binding.startsWith('space.')) {
            // Could validate token exists in token system
          }
        });
      }
    });

    // Validate layout gap token
    if (dsl.layout.gap && !dsl.layout.gap.startsWith('space.')) {
      issues.push('Layout gap must reference a space token (e.g., "space.md")');
    }
  }

  updateRegistry(registry: SectionManifestRegistry): void {
    this.registry = registry;
  }

  validateSectionManifest(manifest: SectionManifest): ValidationResult {
    const errors: string[] = [];

    // Validate required fields
    if (!manifest.id) errors.push('Section manifest must have an id');
    if (!manifest.name) errors.push('Section manifest must have a name');
    if (!manifest.version) errors.push('Section manifest must have a version');
    if (!manifest.category) errors.push('Section manifest must have a category');

    // Validate category
    if (manifest.category && !['element', 'component', 'section'].includes(manifest.category)) {
      errors.push('Section category must be one of: element, component, section');
    }

    // Validate variants
    if (!manifest.variants || !Array.isArray(manifest.variants) || manifest.variants.length === 0) {
      errors.push('Section manifest must have at least one variant');
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  }
}